import * as React from 'react';
import { FC, useRef, useEffect, useState } from 'react';
import { IMedia } from '../../../../../types';
import { style } from 'typestyle';
import Play from '../../../../Icons/PlayPause/Play';

interface VideoPlayerProps {
  post: IMedia;
  preload?: boolean;
  isMuted: boolean;
  isPlaying: boolean;
  togglePlay: () => void;
  autoplay?: boolean;
}

const VideoPlayer: FC<VideoPlayerProps> = ({ 
  post, 
  preload = false, 
  isMuted, 
  isPlaying,
  togglePlay,
  autoplay = false 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [autoplayPrevented, setAutoplayPrevented] = useState(false);
  
  // Handle autoplay
  useEffect(() => {
    if (videoRef.current && autoplay && !isPlaying) {
      videoRef.current.play()
        .then(() => {
          togglePlay();
          setAutoplayPrevented(false);
        })
        .catch(() => {
          console.log('Autoplay was prevented by the browser');
          setAutoplayPrevented(true);
        });
    }
  }, [post.id, autoplay]);

  // Handle play/pause state changes
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play()
          .then(() => {
            setAutoplayPrevented(false);
          })
          .catch(() => {
            togglePlay();
            setAutoplayPrevented(true);
          });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, togglePlay]);

  // Handle mute/unmute state changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  const handlePlayButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up to container
    e.preventDefault(); // Also prevent default behavior
    togglePlay();
  };

  return (
    <div className={styles.container}>
      <video
        ref={videoRef}
        src={post.video}
        poster={post.thumbnail || post.image}
        muted={isMuted}
        playsInline
        loop
        preload={preload || autoplay ? "auto" : "metadata"}
        className={styles.media}
        onClick={togglePlay}
      />
      
      {autoplayPrevented && !isPlaying && (
        <div className={styles.playButtonContainer}>
          <div className={styles.playButtonWrapper}>
            <div 
              className={`${styles.playButton} playButton`} 
              onClick={handlePlayButtonClick}
            >
              <Play size={120} color={'rgba(255, 255, 255, 0.7)'}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;

const styles = {
  container: style({
    position: 'relative',
    width: '100%',
    height: '100%',
  }),
  media: style({
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  }),
  playButtonContainer: style({
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none', 
  }),
  playButtonWrapper: style({
    // This constrains the clickable area to exactly the size of the icon
    display: 'inline-block',
    lineHeight: 0,
    fontSize: 0,
    pointerEvents: 'auto',
  }),
  playButton: style({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  }),
}
